import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Project } from '../shared/models/project';
import { ProjectService } from '../services/project.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ProjectFormComponent } from './project-form.component';
import { IEntity } from '../shared/models/entity';
import { AuthenticationService } from '../services/authentication.service';
import { ProjectInvoiceFormComponent } from './project-invoice-form.component';
import { InvoiceService } from '../services/invoice.service';
import { ProjectInvoiceListComponent } from './project-invoice-list.component';
import { PpsUtilityService } from '../services/pps-utility.service';
import { EntityCategory } from '../shared/models/entity-category';
import { EntityCollection } from '../shared/models/entity-collection';
import { AccessPermission } from '../shared/models/access-permission';
import { ToastService } from '../shared/toast/toast.service';
import { UserContext } from '../shared/models/user-context';
import { BusinessOperation } from '../shared/models/business-operation';
import { Employee } from '../shared/models/employee';
import { format} from 'date-fns';
import { Company, CompanyRepresentative } from '../shared/models/company';
import { ProjectTimesheetComponent } from './project-timesheet.component';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { forkJoin } from 'rxjs';
import { SubSink } from 'subsink';
import { ProjectStatuses } from '../shared/models/project-statuses';
import { ProgressStatuses } from '../shared/models/progress-statuses';


@Component({
  selector: 'pps-projects',
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ProjectsComponent implements OnInit, OnDestroy {
  smallScreen: boolean = false;
  
  items: MenuItem[] = [{label: "Project", icon: "pi-fw fas fa-tractor"}];
  private subs = new SubSink();

  private projectColumnAccessPermission: any = {};
  isLoggedIn: boolean = false;
  spinnerName: string = "ProjectSpinner";

  userContext: UserContext = null;

  projectSearchModel: any = {};
  projectEntities: EntityCollection = new EntityCollection();
  responsibleEngineers: Employee[] = [];
  companies: Company[] = [];
  companyRepresentatives: any = {};

  projects: Project[] = [];
  projectTotal: Project[] = [];
  filteredProjectCount: number = 0;
  activeProjectsOnly = true;

  gridHeaders = [];
  context:any = {};
  selectedRow: any = null;
  selectedRowNode: any = null; //ag-grid RowNode

  exporting: boolean = false;

  topOptions: any = {
    alignedGrids: () => this.bottomApi ? [this.bottomApi] : [],
    onFilterChanged: params => this.onFilterChanged(params),
    onRowClicked: this.onRowClicked,
    //immutableData: true,
    getRowId: param => `${param.data.id}`,
    context: this,
    defaultColDef: {
        resizable: true,
        minWidth: 60,
        floatingFilter: false,
        filter: true,
        sortable: true,
        cellStyle: {borderRightColor:'#eeeeee'}
    },
    suppressHorizontalScroll: true
  };

  bottomOptions: any = {
    alignedGrids: () => this.topApi ? [this.topApi] : [],
    defaultColDef: {
      resizable: true,
      minWidth: 60
    }
  };

  private topApi: any;
  private bottomApi: any;
  private dateFilterParams = {
    comparator: this._util.filterDateComparator,
    filterOptions: ["equals", "lessThan", "greaterThan"],
    defaultOption: "greaterThan"
  };

  private numberFilterParams = {
    filterOptions: ["equals", "lessThan", "greaterThan"],
    defaultOption: "equals"
  };

  constructor(private projectService: ProjectService,
              private invoiceService: InvoiceService,
              private spinner: NgxSpinnerService,
              private authService: AuthenticationService,
              private _util: PpsUtilityService,
              private _toast: ToastService,
              private _dialog: DialogService,
              private _confirmationService: ConfirmationService,
              private breakpointObserver: BreakpointObserver) {
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });
  }

  ngOnInit(): void {
    this.context = { componentParent: this };
    this.loadProjects();
  }

  ngOnDestroy():void{
    this.subs.unsubscribe();
  }

  onTopGridReady(params) {
    this.topApi = params.api;
    // expose api/columnApi on the gridOptions so alignedGrids can use them
    try{
      this.topOptions.api = params.api;
      if ((params as any).columnApi) {
        this.topOptions.columnApi = (params as any).columnApi;
      } else if ((params.api as any).getColumnApi) {
        this.topOptions.columnApi = (params.api as any).getColumnApi();
      }
    }catch(_){ }
  }

  onBottomGridReady(params) {
    this.bottomApi = params.api;
    // ensure gridOptions objects reference the api when the grid is ready
    try{
      this.bottomOptions.api = params.api;
      if ((params as any).columnApi) {
        this.bottomOptions.columnApi = (params as any).columnApi;
      } else if ((params.api as any).getColumnApi) {
        this.bottomOptions.columnApi = (params.api as any).getColumnApi();
      }
    }catch(_){ }
  }
  
  onRowClicked(evt){
    let _this = evt.context.componentParent;
    var selectedRows = _this.topApi.getSelectedRows();
    _this.selectedRow = selectedRows.length === 1 ? selectedRows[0] : null;
    _this.selectedRowNode = evt.node;
  }

  onFirstDataRendered(params) {
    //params.columnApi.autoSizeAllColumns();
  }

  onCellDblClicked(params){
    if (this.canModifyProject()){
      this.openProjectForm(params.data, params.column.colId);
    }
  }

  onFilterChanged(param: any){
    const projects: Project[] = [];
    param.api.forEachNodeAfterFilter((node: any) => {
      if (node && node.data) {
        projects.push(node.data);
      }
    });
    this.filteredProjectCount = projects.length;
    this.projectTotal = this.context.componentParent.calculateProjectsTotal(projects);
    
    this.setGridRowData(this.bottomApi, this.projectTotal, (rows) => {
      this.projectTotal = rows;
    });
  }

  exportProject(){
    this.exporting = true;
    let searchModel = Object.assign({}, this.projectSearchModel);
    searchModel.ipIds = searchModel.ipIds ? searchModel.ipIds.map(s=>s.id) : [];
    searchModel.psIds = searchModel.psIds ? searchModel.psIds.map(s=>s.id) : [];    
    this.subs.sink = this.projectService.downloadProjects(searchModel, () => this.exporting = false);
  }

  loadProjects(){
    let searchModel = Object.assign({}, this.projectSearchModel);
    searchModel.ipIds = searchModel.ipIds ? searchModel.ipIds.map(s=>s.id) : [];
    searchModel.psIds = searchModel.psIds ? searchModel.psIds.map(s=>s.id) : [];
    this.spinner.show(this.spinnerName);

    this.subs.sink = forkJoin([
      this.projectService.getProjectEntities(),
      this.projectService.getProjects(searchModel)
    ]).subscribe(([entitiesRes, projectResponse]) => {
      if (!this.authService.userContext){
        console.log("Waiting for permission settings, re-trying...");
        window.setTimeout(() => this.loadProjects(), 1000);
        return;
      }

      if (entitiesRes.succeed){
        this._loadEntitiesHelper(entitiesRes.result.entities);
        this.responsibleEngineers = entitiesRes.result.responsibleEngineers;
        this.processCompanyAndRepresentative(entitiesRes.result.representatives);
        this.companies = entitiesRes.result.companies;
      } else {
        console.log(`Load entities failed: ${entitiesRes.errors.join(";")}`);
        console.log("Retrying...");
        window.setTimeout(() => this.loadProjects(), 3000);
      }

      this.projectColumnAccessPermission = this.authService.userContext.projectColumnAccessSettings;
      this.gridHeaders = this.getColumnDefs(this.projectColumnAccessPermission);

      if (projectResponse.succeed){
        let projects = projectResponse.result;
        this.projects = this.getProjects(projects);
        this.projectTotal = this.calculateProjectsTotal(this.projects);
        this._toast.succeeded(`${this.projects.length} projects loaded`);
        this.topApi.onFilterChanged();
      }else{
        this._toast.failed(`Load projects failed: ${projectResponse.errors.join(";")}`);
      }
    })
    .add(() => {
      this.spinner.hide(this.spinnerName);
    });

  }

  private getProjects(projects: Project[]): Project[]{
    return this.activeProjectsOnly ? projects.filter(p => this.isActiveProject(p)) : projects;
  }

  private isActiveProject(p: Project){
    if (p.projectStatus?.id === ProjectStatuses.Quoted){
      return true;
    }

    if (p.installationProgress?.id === ProgressStatuses.Completed){
      return true;
    }

    if ((p.projectStatus?.id === ProjectStatuses.POReceived || 
        p.projectStatus?.id === ProjectStatuses.Approved ||
        p.projectStatus?.id === ProjectStatuses.RFQ) &&
        (p.installationProgress?.id !== ProgressStatuses.Pending &&
        p.installationProgress?.id !== ProgressStatuses.Completed &&
        p.installationProgress?.id !== ProgressStatuses.IRCSigned &&
        p.installationProgress?.id !== ProgressStatuses.Invoiced &&
        p.installationProgress?.id !== ProgressStatuses.Paid)){
      return true;
    }

    return false;
  }

  private compareObject(a, b, prop){
    if (a[prop] < b[prop]){
      return -1;
    }

    if (a[prop] > b[prop]){
      return 1;
    }

    return 0;
  }

  private processCompanyAndRepresentative(compReps: CompanyRepresentative[]){
    this.companyRepresentatives = {};
    compReps.forEach(r => {
      if (!this.companyRepresentatives.hasOwnProperty(r.company.id)){
        this.companyRepresentatives[r.company.id] = [{id: r.id, name: r.name, active: r.active}];
      }else{
        this.companyRepresentatives[r.company.id].push({id: r.id, name: r.name, active: r.active});
      }
    });
    
    this.companies = this.companies.sort((a, b) => this.compareObject(a, b, "name"));
    for (let cid in this.companyRepresentatives){
      if (this.companyRepresentatives.hasOwnProperty(cid)){
        this.companyRepresentatives[cid] = this.companyRepresentatives[cid].sort((a, b) => this.compareObject(a, b, "name"));
      }
    }
  }

  private _loadEntitiesHelper(entities: IEntity[]){
    this.projectEntities.jobTypes = [];
    this.projectEntities.materialProcurementStatuses = [];
    this.projectEntities.projectStatuses = [];
    this.projectEntities.areas = [];
    this.projectEntities.clientEngineers = [];
    this.projectEntities.installationProgresses = [];
    entities.forEach(entity => {
      switch(entity.entityCategory){
        case EntityCategory.JobType:
          this.projectEntities.jobTypes.push(entity);
          break;
        case EntityCategory.MaterialProcurementStatus:
          this.projectEntities.materialProcurementStatuses.push(entity);
          break;
        case EntityCategory.ProjectStatus:
          this.projectEntities.projectStatuses.push(entity);
          break;
        case EntityCategory.Area:
          this.projectEntities.areas.push(entity);
          break;
        case EntityCategory.ClientEngineer:
          this.projectEntities.clientEngineers.push(entity);
          break;
        case EntityCategory.InstallationProgress:
          this.projectEntities.installationProgresses.push(entity);
          break;
      }
    });

    this.projectEntities.jobTypes.sort((a, b) => this.compareObject(a, b, "name"));
    this.projectEntities.projectStatuses.sort((a, b) => this.compareObject(a, b, "name"));
    this.projectEntities.areas.sort((a, b) => this.compareObject(a, b, "name"));
    this.projectEntities.clientEngineers.sort((a, b) => this.compareObject(a, b, "name"));
    this.projectEntities.installationProgresses.sort((a, b) => this.compareObject(a, b, "sortNo"));
  }

  calculateProjectsTotal(projects: Project[]) : Project[]{
    let projectTotal = new Project();
    projectTotal.projectNo = "Total";
    projectTotal.totalInvoiceValue = 0;
    projectTotal.budgetHour = 0;
    projectTotal.plannedHour = 0;
    projectTotal.actualHour = 0;
    projectTotal.bankGuaranteeAmount = 0;
    projectTotal.poAmount = 0;
    projectTotal.estimatedCompletionValue = 0;
    projectTotal.completionPercentage = null;

    projectTotal.directLabor = 0;
    projectTotal.plantAndEquipment = 0;
    projectTotal.materialsAndConsumbles = 0;
    projectTotal.management = 0;
    projectTotal.thirdParty = 0;
    projects.forEach(prj => {
      prj.totalInvoiceValue = 0;
      if (prj.invoices){
        prj.invoices.forEach(inv => {
          prj.totalInvoiceValue += inv.invoiceValue || 0;
        });
      }

      projectTotal.totalInvoiceValue += prj.totalInvoiceValue;
      projectTotal.budgetHour += prj.budgetHour || 0;
      projectTotal.plannedHour += prj.plannedHour || 0;
      projectTotal.actualHour += prj.actualHour || 0;
      projectTotal.bankGuaranteeAmount += prj.bankGuaranteeAmount || 0;
      projectTotal.poAmount += prj.poAmount || 0;
      projectTotal.estimatedCompletionValue += prj.estimatedCompletionValue || 0;

      projectTotal.directLabor += prj.directLabor || 0;
      projectTotal.plantAndEquipment += prj.plantAndEquipment || 0;
      projectTotal.materialsAndConsumbles += prj.materialsAndConsumbles || 0;
      projectTotal.management += prj.management || 0;
      projectTotal.thirdParty += prj.thirdParty || 0;
    });

    return [projectTotal];
  }

  openProjectForm(project: Project, colId: string = ""){
    if (project === null){
      project = new Project();
      project.id = 0;
      project.projectNo = this.projectService.getNextProjectNo(this.projects);
    }

    project.commentDate = new Date();

    if ( project.id === 0 || !project.responsibleEngineer ){
      // For new project or the project has NOT been assigned any responsible engineer
      this.projectEntities.responsibleEngineers = this.responsibleEngineers.filter(re => re.active);
    }else{
      if ( project.responsibleEngineer ){
        // terminated engineer should be exclude from the selection list except when this engineer was
        // assigned to a project
        this.projectEntities.responsibleEngineers = [];
        this.responsibleEngineers.forEach(e => {
          if ( (!e.active && e.id === project.responsibleEngineer.id) || e.active){
            this.projectEntities.responsibleEngineers.push(e);
          }
        });
      }
    }

    let dialog = this._dialog.open(ProjectFormComponent, 
      {
        header: project.id === 0 ? "Add Project" : `Edit Project ${project.projectNo}`, 
        styleClass: "size-md2",
        data:{
          companies: this.companies,
          representatives: this.companyRepresentatives,
          currentUser: this.authService.userContext.zoesenUser,
          ppsEntites: this.projectEntities,
          ppsProject: Object.assign({}, project),
          ppsProjectColumnAccessPermission: this.projectColumnAccessPermission,
          sourceColumnId: colId
        },
        draggable: true
      });

    this.subs.sink = dialog.onClose.subscribe(prj => {
      if (prj){
        this.saveProject(prj);
      }
    });
  }

  saveProject(project: Project){
    if (project.id === 0){
      this.subs.sink = this.projectService
        .addProject(project)
        .subscribe(response => {
          if (response.succeed){
            this.projects.unshift(response.result);
            this.resetProjectGrid(project.id, false);
            this.flashRow(response.result.id);
            this._toast.succeeded(`Project [${project.projectNo}] created successfully`);
          }else{
            this._toast.failed(`Save project [${project.projectNo}] failed: ${response.errors.join(";")}`);
          }
        });
    }else{
      //update project
      this.subs.sink = this.projectService
        .updateProject(project)
        .subscribe(response => {
          if (response.succeed){
            var foundIndex = this.projects.findIndex(x => x.id == response.result.id);
            if (foundIndex !== -1){
              let selectedProject = this.projects[foundIndex] = response.result;

              selectedProject = response.result;
              this.resetProjectGrid(selectedProject.id, false);
              this.flashRow(response.result.id);
              this._toast.succeeded(`Project [${selectedProject.projectNo}] updated successfully`);
            }else{
              this._toast.failed(`Update project ${response.result.id} failed`);
            }
          }else{
            this._toast.failed(`Update project [${project.projectNo}] failed: ${response.errors.join(";")}`);
          }
        });
    }
  }

  deleteProject(project: Project){
    if (project === null){
      return;
    }

    this._confirmationService.confirm({
      header: `Delete Project ${project.projectNo}`,
      message: `Are you sure that you want to delete it?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => {
        this.subs.sink = this.projectService.deleteProject(project.id)
           .subscribe(res => {
             var foundIndex = this.projects.findIndex(x => x.id == project.id);
             if (foundIndex !== -1 ){
               this.projects.splice(foundIndex, 1);
               this.selectedRow = null;
               this.selectedRowNode = null;
               this.resetProjectGrid(0, false);

               if (foundIndex < this.projects.length){
                let nextRow = this.topApi.getRowNode(this.projects[foundIndex].id);
                if (nextRow){
                  nextRow.setSelected(true);
                  this.selectedRowNode = nextRow;
                  this.selectedRow = nextRow.data;
                }
              }

               this._toast.succeeded(`Project [${project.projectNo}] deleted`);
            }
          });
      }
    });
  }

  addInvoice(project: Project){
    if (project === null){
      return;
    }

    let _this = this;
    let dialog = this._dialog.open(ProjectInvoiceFormComponent, {
      header: `Create invoice for ${project.projectNo}`,
      styleClass: "size-sm",
      draggable: true
    });

    this.subs.sink = dialog.onClose.subscribe(invoiceFormData => {
      if (invoiceFormData){
        _this.saveInvoice(project.id, invoiceFormData);
      }},
      _ => { }
    );
  }

  saveInvoice(projectId: number, formData: FormData){
    this.subs.sink = this.invoiceService.createInvoice(projectId, formData)
      .subscribe(res => {
        if (res.succeed){
          var foundIndex = this.projects.findIndex(x => x.id == projectId);
          if (!this.projects[foundIndex].invoices){
            this.projects[foundIndex].invoices = [];
          }

          this.projects[foundIndex].invoices.push(res.result);

          this.resetInvoice(projectId, this.projects[foundIndex].invoices);
          this._toast.succeeded(`Invoice created for project [${this.projects[foundIndex].projectNo}]`);
        }else{
          this._toast.failed(`Create invoice for project [${this.projects[foundIndex].projectNo}] failed: ${res.errors.join(";")}`);
        }
      });
  }

  viewProjectInvoices(project: Project){
    if (project === null){
      return;
    }

    let dialog = this._dialog.open(ProjectInvoiceListComponent, {
      header: `Project Invoices: ${project.invoices.length}`,
      styleClass: this.smallScreen ? "size-100" : "size-md",
      data: {
        parent: this,
        project: project
      },
      draggable: true
    });
    //dialog.componentInstance.setData(this, project, project.invoices);
    this.subs.sink = dialog.onClose.subscribe(
      invoiceFormData => { },
      reason => { }
    );
  }
  // This method is used to deleteInvoie. DO NOT REMOVE IT
  resetInvoice(projectId, invoices){
    var prjIndex = this.projects.findIndex(p => p.id === projectId);
    if (prjIndex !== -1){
      this.projects[prjIndex].invoices = invoices;
      this.resetProjectGrid(projectId, true);

      var node = this.topApi.getRowNode(projectId);
      if (node){
        node.setData(this.projects[prjIndex]);
      }
    }
  }

  viewProjectTimesheet(project: Project){
    if (project === null){
      return;
    }

    this._dialog.open(ProjectTimesheetComponent, 
    {
      header: `Timesheet for project ${project.projectNo}`,
      styleClass: this.smallScreen ? "size-100" : "size-lg",
      data:{
        project: project
      },
      draggable: true
    });
  }

  //
  // This is to update the values of the project grid and make sure the selected
  // row is rendered correctly
  private resetProjectGrid(selectedProjectId: number, ensureVisible: boolean){
    this.projectTotal = this.calculateProjectsTotal(this.projects);

    // reference:
    // https://stackoverflow.com/questions/55723337/ag-grid-how-to-scroll-to-last-known-position
    // if ( ensureVisible ){
    //   this.topOptions.immutableData = false;
    // }

    this.setGridRowData(this.topApi, this.projects, (rows) => {
      this.projects = rows;
    });
    
    if (selectedProjectId !== 0){
      var node = this.topApi.getRowNode(selectedProjectId);
      if (node){
        if (ensureVisible){
          this.topApi.ensureIndexVisible(node.rowIndex, 'middle');
        }
        node.setSelected(true);
        this.selectedRow = node.data;
      }
    }

    // if ( ensureVisible ){
    //   this.topOptions.immutableData = true;
    // }
  }

  private setGridRowData(gridApi: any, rows: Project[], fallback?: (rows: Project[]) => void): void {
    if (!gridApi) {
      if (fallback) {
        fallback(rows);
      }
      return;
    }

    gridApi.setGridOption('rowData', rows);
  }

  canViewProject(): boolean{
    return this.authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ViewProject,
      BusinessOperation.CreateProject,
      BusinessOperation.ModifyProject,
      BusinessOperation.DeleteProject
    ]);
  }

  canCreateProject(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.CreateProject);
  }

  canModifyProject(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.ModifyProject);
  }

  canDeleteProject(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.DeleteProject);
  }

  canExportProject(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.ExportProject);
  }

  canCreateProjectInvoice(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.CreateProjectInvoice);
  }

  canViewProjectInvoice(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.ViewProjectInvoice);
  }

  canDeleteInvoice(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.DeleteProjectInvoice);
  }

  canUpdateInvoice(): boolean{
    return this.authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.CreateProjectInvoice,
      BusinessOperation.DeleteProjectInvoice
    ]);
  }

  canViewTimeSheet(): boolean{
    return this.authService.canUserDoAnyOfTheOperartions([
      BusinessOperation.ViewTimeSheet,
      BusinessOperation.EnterTimeSheet,
      BusinessOperation.ModifyTimeSheet,
      BusinessOperation.UpdatePastTimeSheet,
      BusinessOperation.ExportTimeSheet
    ]);
  }

  private getTotalColumns(){
    return {
      "ProjectAttachment": {
        headerName: '',
        field:'hasAttachment',
        width: 20,
        pinned: true,
        resizable: false,
        minWidth: 20,
        floatingFilter: false,
        filter: false,
        sortable: true,
        cellRenderer: (params) => params.value ? "<i class='fas fa-paperclip'></i>" :  ""
      },
      "ProjectNo": {
        headerName: 'Project No',
        field:'projectNo',
        width: 90,
        pinned: true
      },
      "Description": {
        headerName: 'Description',
        field:'description',
        width: 500,
        headerClass: this.getProjectInfoHeaderClass,
        tooltipField: 'description'
      },
      "JobType": {
        headerName: 'Job Type',
        field:'jobType.name',
        width: 120,
        headerClass: this.getProjectInfoHeaderClass
      },
      "ProjectStatus": {
        headerName: 'Project Status',
        field:'projectStatus.name',
        width: 130,
        headerClass: this.getProjectInfoHeaderClass
      },
      "ResponsibleEngineer": {
        headerName: 'Responsible Engineer',
        field:'employeeId',
        width: 180,
        headerClass: this.getProjectInfoHeaderClass,
        valueGetter: this.getResponsibleEngineer
      },
      "Client": {
        headerName: 'Client',
        field:'client.name',
        width: 140,
        headerClass: this.getProjectInfoHeaderClass,
        tooltipField: 'client.name'
      },
      "ClientEngineers": {
        headerName: 'Client Representatives',
        field:'clientEngineerId',
        width: 160,
        headerClass: this.getProjectInfoHeaderClass,
        valueGetter: this.getClientEngineers
      },
      "ProjectComment": {
        headerName: 'Project Comment',
        field:'projectComment',
        width: 200,
        headerClass: this.getProjectInfoHeaderClass,
        tooltipField: 'projectComment'

      },
      "QuotingDeadline": {
        headerName: 'Quoting Deadline',
        field:'quotingDeadline',
        filter: 'agDateColumnFilter',
        filterParams: this.dateFilterParams,
        browserDatePicker: true,
        width: 170,
        comparator: (date1, date2) => this._util.dateTimeComparator(date1, date2),
        headerClass: this.getProjectInfoHeaderClass,
        valueGetter: this.getQuotingDeadline
      },
      "MaterialProcurementStatus": {
        headerName: 'Material Status - Arrival Date',
        field:'materialProcurementStatus.name',
        width: 220,
        headerClass: this.getProjectInfoHeaderClass,
        valueGetter: (params) => this.getMaterialProcurementStatus(this, params)
      },
      "InstallationProgress": {
        headerName: 'Progress',
        field:'installationProgress.name',
        width: 100,
        headerClass: this.getProjectInfoHeaderClass
      },
      "InvoiceNo": {
        headerName: 'Invoices',
        field:'invoices',
        headerClass: this.getProjectInfoHeaderClass,
        valueGetter: this.getInvoices
      },
      "InvoiceValue": {
        headerName: 'Total Invoice Value',
        field:'totalInvoiceValue',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 150,
        cellRenderer: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field]),
        cellStyle: this._util.getComputedCellStyle(this._util.getNumericCellStyle({borderRightColor:'#eeeeee', color:'#226622'})),
        headerClass: this.getProjectInfoHeaderClass
      },
      "TargetStartDate": {
        headerName: 'Target Start Date',
        field:'targetStartDate',
        filter: 'agDateColumnFilter',
        filterParams: this.dateFilterParams,
        width: 170,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        headerClass: this.getProjectInfoHeaderClass,
        valueGetter: this.getDateValue
      },
      "TargetCompletionDate": {
        headerName: 'Target Completion Date',
        field:'targetCompletionDate',
        filter: 'agDateColumnFilter',
        filterParams: this.dateFilterParams,
        width: 170,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        headerClass: this.getProjectInfoHeaderClass,
        valueGetter: this.getDateValue
      },
      "BudgetHour": {
        headerName: 'Budget Hours',
        field:'budgetHour',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 120,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => params.context.componentParent._util.formatNumber(params.data[params.colDef.field], 2),
        headerClass: this.getProjectInfoHeaderClass
      },
      "PlannedHour": {
        headerName: 'Planned Hours',
        field:'plannedHour',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 120,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => params.context.componentParent._util.formatNumber(params.data[params.colDef.field], 2),
        headerClass: this.getProjectInfoHeaderClass
      },
      "ActualHour": {
        headerName: 'Spent Hours',
        field:'actualHour',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width:120,
        cellStyle: this._util.getComputedCellStyle(this._util.getNumericCellStyle({borderRightColor:'#eeeeee', color:'#226622'})),
        cellRenderer: params => params.context.componentParent._util.formatNumber(params.data[params.colDef.field], 2),
        headerClass: this.getProjectInfoHeaderClass
      },
      "EarnedHour": {
        headerName: 'Earned Hours',
        field:'earnedHours',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 120,
        cellStyle: this._util.getComputedCellStyle(this._util.getNumericCellStyle({borderRightColor:'#eeeeee', color:'#226622'})),
        cellRenderer: params => {
          let prj = params.data;
          let earnedHours = prj.budgetHour - prj.actualHour;
          return params.context.componentParent._util.formatNumber(earnedHours, 2);
        },
        valueGetter: params => {
          let prj = params.data;
          let earnedHours = prj.budgetHour - prj.actualHour;
          return earnedHours;
        },
        headerClass: this.getProjectInfoHeaderClass
      },
      "CompletionPercentage": {
        headerName: 'Completion %',
        field:'completionPercentage',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 120,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: this.progressCellRenderer,
        sortable: true,
        headerClass: this.getProjectInfoHeaderClass
      },
      "WorkOrder": {
        headerName: 'WorkOrder',
        field:'workOrder',
        width: 100,
        headerClass: this.getProjectInfoHeaderClass
      },
      "ContractNumber": {
        headerName: 'Contract#',
        field:'contractNumber',
        width: 120,
        headerClass: this.getProjectInfoHeaderClass
      },
      "BankGuaranteeAmount": {
        headerName: 'Bank Guarantee Amount',
        field:'bankGuaranteeAmount',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 180,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field]),
        headerClass: this.getProjectInfoHeaderClass
      },
      "BankGuaranteeExpirationDate": {
        headerName: 'Bank Guarantee Expiration Date',
        field:'bankGuaranteeExpirationDate',
        filter: 'agDateColumnFilter',
        filterParams: this.dateFilterParams,
        width: 170,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        headerClass: this.getProjectInfoHeaderClass,
        valueGetter: this.getDateValue
      },
      "PoNumber": {
        headerName: 'PO#',
        field:'poNumber',
        width: 100,
        headerClass: this.getProjectInfoHeaderClass
      },
      "PoAmount": {
        headerName: 'PO Value',
        field:'poAmount',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 120,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field]),
        headerClass: this.getProjectInfoHeaderClass
      },
      "PoValueWithGST": {
        headerName: 'PO Value With GST',
        field:'poAmount',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 150,
        cellStyle: this._util.getComputedCellStyle(this._util.getNumericCellStyle({borderRightColor:'#eeeeee', color:'#226622'})),
        cellRenderer: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field] * params.context.componentParent._util.getGstRate()),
        valueGetter: params => params.data[params.colDef.field] * params.context.componentParent._util.getGstRate(),
        headerClass: this.getProjectInfoHeaderClass
      },
      "EstimatedCompletionValue": {
        headerName: 'Estimated Completion Value',
        field:'estimatedCompletionValue',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 200,
        cellStyle: this._util.getComputedCellStyle(this._util.getNumericCellStyle({borderRightColor:'#eeeeee', color:'#226622'})),
        cellRenderer: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field]),
        headerClass: this.getProjectInfoHeaderClass
      },
      "DirectLabor": {
        headerName: 'Direct Labour',
        field:'directLabor',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 120,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field]),
        headerClass: this.getProjectInfoHeaderClass
      },
      "PlantAndEquipment": {
        headerName: 'Plant And Equipment',
        field:'plantAndEquipment',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 180,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field]),
        headerClass: this.getProjectInfoHeaderClass
      },
      "MaterialsAndConsumbles": {
        headerName: 'Materials And Consumbles',
        field:'materialsAndConsumbles',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 180,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field]),
        headerClass: this.getProjectInfoHeaderClass
      },
      "Management": {
        headerName: 'Management',
        field:'management',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 120,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field]),
        headerClass: this.getProjectInfoHeaderClass
      },
      "ThirdParty": {
        headerName: '3rd Party',
        field:'thirdParty',
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        width: 120,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field]),
        headerClass: this.getProjectInfoHeaderClass
      }
    };
  }

  getColumnDefs(columnsWithPermission){
    let totalColumn = this.getTotalColumns();
    let columns = [];
    for (var columnName in totalColumn){
      if (columnsWithPermission.hasOwnProperty(columnName) && columnsWithPermission[columnName].accessPermission !== AccessPermission.NoAccess){
        totalColumn[columnName].sortNo = columnsWithPermission[columnName].sortNo;
        columns.push(totalColumn[columnName]);
      }
    }

    columns = columns.sort((c1,c2) => c1.sortNo - c2.sortNo);

    // This extra column is to avoid cut off the last column
    columns.push({ // Extra column
      headerName:"",
      field:"",
      filter:false,
      sortable:false,
      width:20
    });
    
    return columns;
  }

  getProjectInfoHeaderClass(){
    return ["project-header", "project-info"];
  }

  getProjectStatusHeaderClass(){
    return ["project-header", "project-status"];
  }

  getProjectPoHeaderClass(){
    return ["project-header", "po-info"];
  }

  hasAccess(colName){
    return this.projectColumnAccessPermission[colName] && (
      this.projectColumnAccessPermission[colName].accessPermission === AccessPermission.ReadAccess ||
      this.projectColumnAccessPermission[colName].accessPermission === AccessPermission.WriteAccess ||
      this.projectColumnAccessPermission[colName].accessPermission === AccessPermission.ExecuteAccess
    );
  }

  flashRow(id){
    let rowNode = this.topApi.getRowNode(id);
    if (rowNode){
      this.topApi.flashCells({rowNodes: [rowNode]});
    }    
  }  

  private getDateValue(params){
    if (params.data[params.colDef.field]){
      let value = params.context.componentParent._util.getDateString(params.data[params.colDef.field]);
      return value;
    }

    return "";
  }

  private getResponsibleEngineer(params){
    let project: Project = params.data;
    let employeeName = params.context.componentParent._util.getEmployeeName(project.responsibleEngineer);
    return employeeName;
  }

  private getClientEngineers(params){
    let rowData: Project = params.data;
    let clientEngineers: string[] = [];
    if (Array.isArray(rowData.clientEngineers)){
      rowData.clientEngineers.forEach(emp => {
        clientEngineers.push(emp.name);
      })
    }

    return clientEngineers.join(", ");
  }

  private getInvoices(params){
    let rowData: Project = params.data;
    let invoices: string[] = [];
    if (Array.isArray(rowData.invoices)){
      rowData.invoices.forEach(inv => {
        invoices.push(inv.invoiceNo);
      })
    }
    return invoices.join(", ");
  }

  private getQuotingDeadline(params){
    let project: Project = params.data;
    if (project.quotingDeadline){
      var date = new Date(project.quotingDeadline);
      //date.setHours(project.quotingDeadlineTime.hour, project.quotingDeadlineTime.minute, project.quotingDeadlineTime.second);
      
      return format(date, "dd/MM/yyyy hh:mm a");
    }
    
    return project.quotingDeadline;
  }

  private getMaterialProcurementStatus(caller, params){
    let project: Project = params.data;
    let name = project.materialProcurementStatus?.name?.toLowerCase();
    if (name === "po raised" || name === "under delivery"){
      let dateStr = project.materialEstimateDeliveryDate ? " - " + caller._util.getDateString(project.materialEstimateDeliveryDate) : "";
      let deliveryDate = params.context.componentParent.hasAccess("MaterialEstimateDeliveryDate") ? dateStr : "";
      return `${project.materialProcurementStatus?.name ?? ""}${deliveryDate}`;
    }

    if (name === "material arrived"){
      let dateStr = project.materialActualArrivalDate ? " - " + caller._util.getDateString(project.materialActualArrivalDate) : "";
      let deliveryDate = params.context.componentParent.hasAccess("MaterialActualArrivalDate") ? dateStr : "";
      return `${project.materialProcurementStatus?.name ?? ""}${deliveryDate}`;
    }
    
    return project.materialProcurementStatus?.name;
  }

  private progressCellRenderer(params: any) {
    const value = Math.round(params.value || 0);
    return `
      <div style="display: flex; align-items: center; width: 100%; height: 100%;">
        <progress 
          value="${value}" 
          max="100" 
          style="width: 70%; height: 16px; margin-right: 8px;">
        </progress>
        <span>${value}%</span>
      </div>
    `;
  }
}
